import './../../styles/AddHotspotsOnMap.scss';
import { HiMapPin } from "react-icons/hi2";
import { useEffect, useMemo, useState } from 'react';
import { Select, MenuItem, Button, FormControl, InputLabel, Tooltip, TextField, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ASSETS_PATH } from '../../constants';
import { IMapData, ISpace, ILocation, IHotspotNearMe } from '../../models/Map';

let selectedHotspot: HTMLElement | null = null;
let gHotspots: IHotspotNearMe[] = [];
let mapHeight: number = 0;

interface ManageHotspotsNearMeProps {
    onClose: () => void;
    mapData: Partial<IMapData>;
    spaces: ISpace[];
    locations: ILocation[];
    onUpdateHotspots: (hotspots: IHotspotNearMe[]) => void;
}

const ManageHotspotsNearMe: React.FC<ManageHotspotsNearMeProps> = ({ onClose, mapData, spaces, locations, onUpdateHotspots }) => {

    const [hotspots, setHotspots] = useState<IHotspotNearMe[]>(mapData.map_hotspots_near_me || []);
    const [selectedType, setSelectedType] = useState<'space' | 'location'>('space');
    const [selectedId, setSelectedId] = useState<number>(0);
    const [hoveredHotspotIndex, setHoveredHotspotIndex] = useState<number | null>(null);
    const [groups, setGroups] = useState<string[]>(['Default']);
    const [newGroupName, setNewGroupName] = useState<string>('');
    const [selectedGroup, setSelectedGroup] = useState<string>('Default');
    const [sortConfig, setSortConfig] = useState<{ key: 'group' | 'type'; direction: 'ascending' | 'descending' } | null>(null);


    // Update global reference when hotspots change
    useEffect(() => {
        gHotspots = hotspots;
    }, [hotspots]);

    const onHotspotDown = (e: React.MouseEvent<SVGElement>): void => {
        selectedHotspot = e.currentTarget as unknown as HTMLElement;
        document.addEventListener('mousemove', onHotspotMove);
    }

    const onHotspotMove = (e: MouseEvent): void => {
        if (!selectedHotspot) return;

        const left = parseInt(selectedHotspot.style.left);
        const top = parseInt(selectedHotspot.style.top);

        let newLeft = e.movementX + left;
        newLeft = newLeft < 0 ? 0 : newLeft;
        newLeft = newLeft > 800 ? 800 : newLeft;

        let newTop = e.movementY + top;
        newTop = newTop < 0 ? 0 : newTop;
        newTop = newTop > mapHeight ? mapHeight : newTop;

        selectedHotspot.style.left = newLeft + 'px';
        selectedHotspot.style.top = newTop + 'px';

        // Update the corresponding hotspot in the array
        const indexAttr = selectedHotspot.getAttribute('data-index');
        if (indexAttr) {
            const index = parseInt(indexAttr);
            if (!isNaN(index) && gHotspots[index]) {
                gHotspots[index].x = newLeft;
                gHotspots[index].y = newTop;
                setHotspots([...gHotspots]);
            }
        }
    }

    const onAddHotspot = (): void => {
        if (selectedId === 0) return;

        const exists = hotspots.some(h => 
            (selectedType === 'space' && h.space_id === selectedId) ||
            (selectedType === 'location' && h.location_id === selectedId)
        );

        if (exists) {
            alert(`${selectedType === 'space' ? 'Space' : 'Location'} already added!`);
            return;
        }

        const newHotspot: IHotspotNearMe = {
            space_id: selectedType === 'space' ? selectedId : 0,
            location_id: selectedType === 'location' ? selectedId : 0,
            group: selectedGroup,
            x: 100,
            y: 100
        };

        setHotspots(prev => [...prev, newHotspot]);
        setSelectedId(0);
        setSelectedGroup('Default');
    }

    const onRemoveHotspot = (index: number): void => {
        setHotspots(prev => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    }

    const onUpdateAndClose = () => {
        const validHotspots = hotspots.filter(h => {
            const groupHotspots = hotspots.filter(hotspot => hotspot.group === h.group);
            return groupHotspots.length > 0;
        });

        onUpdateHotspots(validHotspots);
        onClose();
    }

    const getHotspotDisplayName = (hotspot: IHotspotNearMe): string => {
        if (hotspot.space_id && hotspot.space_id !== 0) {
            const space = spaces.find(s => s.space_id === hotspot.space_id);
            if (space) {
                return space.space_name;
            }
        } else if (hotspot.location_id && hotspot.location_id !== 0) {
            const location = locations.find(l => l.location_id === hotspot.location_id);
            if (location) {
                return location.location_name;
            }
        }
        return "Untitled";
    };

    const getHotspotColor = (hotspot: IHotspotNearMe): string => {
        return hotspot.space_id && hotspot.space_id !== 0 ? 'blue' : 'green';
    };

    const setMapHeightFunc = (): void => {
        const mapElement = document.getElementById('map-near-me');
        if (mapElement) {
            mapHeight = mapElement.offsetHeight;
            const hotspotsBodyElement = document.getElementById('hotspots-body-near-me');
            if (hotspotsBodyElement) {
                hotspotsBodyElement.style.height = mapHeight + 'px';
            }
        }
    }

    useEffect(() => {
        setMapHeightFunc();

        setTimeout(() => {
            setMapHeightFunc();
        }, 1000);

        setTimeout(() => {
            setMapHeightFunc();
        }, 2000);

        setTimeout(() => {
            setMapHeightFunc();
        }, 10000);

        document.addEventListener('mouseup', () => {
            document.removeEventListener('mousemove', onHotspotMove);
            selectedHotspot = null;
        });
    });

    const handleAddGroup = () => {
        if (newGroupName && !groups.includes(newGroupName)) {
            setGroups(prev => [...prev, newGroupName]);
            setNewGroupName('');
        }
    };

    const handleGroupChange = (hotspotIndex: number, newGroup: string) => {
        const updatedHotspots = [...hotspots];
        updatedHotspots[hotspotIndex].group = newGroup;
        setHotspots(updatedHotspots);
    };

    const sortedHotspots = useMemo(() => {
        let sortableHotspots = [...hotspots];
        if (sortConfig !== null) {
            sortableHotspots.sort((a, b) => {
                let aValue: string, bValue: string;
                if (sortConfig.key === 'group') {
                    aValue = a.group || 'Default';
                    bValue = b.group || 'Default';
                } else { // type
                    aValue = a.space_id ? 'space' : 'location';
                    bValue = b.space_id ? 'space' : 'location';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableHotspots;
    }, [hotspots, sortConfig]);



    return (
        <>
            <div className="pop-map-background"></div>
            <div className='pop-map'>
                <div className='pop-header'>
                    <div className='map-name'>
                        Manage Near Me Hotspots
                    </div>
                    <div>
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                            <InputLabel>Group</InputLabel>
                            <Select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value as string)}>
                                {groups.map(g => (
                                    <MenuItem key={g} value={g}>{g}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                            <InputLabel>Type</InputLabel>
                            <Select value={selectedType} onChange={(e) => { setSelectedType(e.target.value as 'space' | 'location'); setSelectedId(0); }}>
                                <MenuItem value="space">Space</MenuItem>
                                <MenuItem value="location">Location</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 220 }}>
                            <InputLabel>Select {selectedType}</InputLabel>
                            <Select value={selectedId} onChange={(e) => setSelectedId(e.target.value as number)}>
                                <MenuItem value={0}>Select {selectedType}</MenuItem>
                                {selectedType === 'space' &&
                                    spaces
                                        .filter(s => !hotspots.some(h => h.space_id === s.space_id))
                                        .map((s, index) => (
                                            <MenuItem key={`s${index}`} value={s.space_id}>{s.space_name}</MenuItem>
                                        ))}
                                {selectedType === 'location' &&
                                    locations
                                        .filter(l => !hotspots.some(h => h.location_id === l.location_id))
                                        .map((l, index) => (
                                            <MenuItem key={`l${index}`} value={l.location_id}>{l.location_name}</MenuItem>
                                        ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" onClick={onAddHotspot} disabled={selectedId === 0} sx={{ m: 1 }}>
                            Add Hotspot
                        </Button>
                    </div>
                </div>
                <div className='pop-body' style={{ display: 'flex', gap: '20px' }}>
                    <div className='hotspots-body' id='hotspots-body-near-me' style={{ flex: '1' }}>
                        <img src={`${ASSETS_PATH}/${mapData.map_image_2d}`} id="map-near-me" />
                        {hotspots.map((hotspot, index) => (
                            <Tooltip
                                title={getHotspotDisplayName(hotspot)}
                                key={index}
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        sx: { backgroundColor: 'black', color: 'white' }
                                    },
                                    arrow: { sx: { color: 'black' } }
                                }} arrow>
                                <HiMapPin
                                    className='hotspot'
                                    data-index={index}
                                    onMouseDown={(e) => onHotspotDown(e)}
                                    style={{
                                        top: hotspot.y + 'px',
                                        left: hotspot.x + 'px',
                                        fill: hoveredHotspotIndex === index ? 'orange' : getHotspotColor(hotspot),
                                        cursor: 'move',
                                        fontSize: hoveredHotspotIndex === index ? '48px' : '36px',
                                        transition: 'font-size 0.2s, fill 0.2s',
                                        transform: hoveredHotspotIndex === index ? 'translate(-24px, -48px)' : 'translate(-18px, -36px)',

                                    }}
                                />
                            </Tooltip>
                        ))}
                    </div>
                    <div style={{ width: '250px', overflowY: 'auto', maxHeight: '600px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <TextField
                                label="New Group Name"
                                variant="standard"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.target.value)}
                                sx={{ m: 1, minWidth: 150 }}
                            />
                            <Button onClick={handleAddGroup} variant="outlined" sx={{ m: 1 }}>Add Group</Button>
                        </div>
                        <h3 style={{ marginTop: 0 }}>Added Hotspots</h3>
                        {hotspots.length === 0 && <p>No hotspots added yet</p>}
                        {groups.map((group, groupIndex) => {
                            const groupHotspots = sortedHotspots.map((h, i) => ({ ...h, originalIndex: i })).filter(h => (h.group || 'Default') === group);
                            if (groupHotspots.length === 0) return null;
                            return (
                                <div key={groupIndex}>
                                    <h4>{group}</h4>
                                    {groupHotspots.map((hotspot) => {
                                        const index = hotspot.originalIndex;
                                        const isHovered = hoveredHotspotIndex === index;
                                        return (<div key={index}
                                            onMouseEnter={() => setHoveredHotspotIndex(index)}
                                            onMouseLeave={() => setHoveredHotspotIndex(null)}
                                            style={{                                                
                                                border: isHovered ? '2px solid orange' : '1px solid #ccc',
                                                borderRadius: '4px',
                                                backgroundColor: isHovered ? '#fff5e6' : '#f9f9f9',                                                
                                                padding: '5px',
                                                marginBottom: '10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}
                                        >
                                            <HiMapPin style={{ fill: getHotspotColor(hotspot), flexShrink: 0 }} />
                                            <div style={{ flexGrow: 1 }}>
                                                <strong>{getHotspotDisplayName(hotspot)}</strong>
                                                <FormControl fullWidth size="small" sx={{ my: 0.5 }}>
                                                    <Select value={hotspot.group || 'Default'} onChange={(e) => handleGroupChange(index, e.target.value as string)} sx={{ fontSize: '0.8rem' }}>
                                                        {groups.map(g => <MenuItem key={g} value={g} sx={{ fontSize: '0.8rem' }}>{g}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                            <IconButton onClick={() => onRemoveHotspot(index)} size="small" color="error" sx={{ flexShrink: 0 }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </div>)
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className='pop-footer'>
                    <Button variant='contained' onClick={onUpdateAndClose}>Update and Close</Button>
                    <Button onClick={onClose}>Cancel</Button>
                </div>
            </div>
        </>
    )
}

export default ManageHotspotsNearMe;
